import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import XLSIcon from "../../assets/icons/excel-icon.svg";

import Primary from "components/Typography/Primary";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import EnhancedTableHead from "../common/EnhancedTableHead";
import Loading from "../common/Loading";

import { actionCreators as actionCreatorsAdmin } from "../../_actions/adminActions";
import { PAGE_SIZE } from "../../constants";
import { yearList, currentYear } from "../../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  filterDropdown: {
    minWidth: "120px"
  },
  expanded: {
    flex: 1
  },
  XLSIcon: {
    width: "20px",
    marginRight: "5px",
    marginTop: "-5px"
  }
}));

const headCells = [
  {
    id: "chapterName",
    numeric: false,
    disablePadding: false,
    label: "Global Analysis Reports"
  },
  {
    id: "download",
    disablePadding: false,
    label: ""
  }
];

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const AnalysisReport = props => {
  const classes = useStyles();
  const [fiscalYear, setFiscalYear] = useState(currentYear);
  const history = useHistory();

  const { getGlobalAnalysisReport, isFetching } = props;

  const handleFiscalYear = useCallback(({ target: { value } }) => {
    setFiscalYear(value);
  }, []);

  const handleViewReport = useCallback(
    type => () => {
      getGlobalAnalysisReport(type, fiscalYear);
    },
    [history, fiscalYear]
  );

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">Global Analysis Reports</Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Grid item className={classes.marginBottom}>
            <div style={{ maxWidth: "700px", marginTop: "25px" }}>
              Only chapters that have an <b>accepted financial report</b> and
              their <b>member count</b> reported for the selected year will be
              included in each report.
            </div>
          </Grid>
          <FormControl
            variant="outlined"
            className={classes.filterDropdown}
            style={{ float: "right" }}
          >
            <InputLabel id="fiscal-year-select">Fiscal Year</InputLabel>
            <Select
              labelId="fiscal-year-select"
              value={fiscalYear}
              onChange={handleFiscalYear}
              label="Fiscal Year"
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              {yearList.map(value => (
                <MenuItem value={value} key={`fiy-${value}`}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead rowCount={4} headCells={headCells} />
              <TableBody>
                {isFetching.CHAPTER_SUMMARY ? (
                  <StyledTableRow>
                    <TableCell colSpan={3} align="center">
                      <Loading />
                    </TableCell>
                  </StyledTableRow>
                ) : (
                  <>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("small")}
                    >
                      <TableCell component="th" scope="row">
                        Small Multi-Chapter Comparative Analysis
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("medium")}
                    >
                      <TableCell component="th" scope="row">
                        Medium Multi-Chapter Comparative Analysis
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("large")}
                    >
                      <TableCell component="th" scope="row">
                        Large Multi-Chapter Comparative Analysis
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("cash")}
                    >
                      <TableCell component="th" scope="row">
                        Cash Reserve Report
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("employment")}
                    >
                      <TableCell component="th" scope="row">
                        Chapter Employment Statistics
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("fiscal")}
                    >
                      <TableCell component="th" scope="row">
                        Chapter Fiscal Year Net Statistics
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={1}
                      className={classes.cursorPointer}
                      onClick={handleViewReport("employmentDetails")}
                    >
                      <TableCell component="th" scope="row">
                        Chapter Employment Details Report
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <img src={XLSIcon} className={classes.XLSIcon} />
                        Download XLS
                      </TableCell>
                    </StyledTableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  reports: state.adminReducer.reports,
  chapterList: state.adminReducer.chapterList,
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AnalysisReport);
